// Accounts, roles and the two things every corner of the UI asks about one.

import type { Lang } from "../../i18n";

export type Role = "admin" | "editor" | "viewer";

// Ranked, mirroring `rank` in jt-web extract / jt-entity: a check for editor also admits an admin, so
// neither side has to enumerate the roles above the one it names.
const ROLE_RANK: Record<Role, number> = { viewer: 0, editor: 1, admin: 2 };

export function atLeast(role: Role, minimum: Role): boolean {
  return ROLE_RANK[role] >= ROLE_RANK[minimum];
}

/**
 * Mirrors `MIN_PASSWORD_LENGTH` in rust/bins/api/src/routes/users.rs. The server is the real check; this is what
 * the four forms that set a password hint with, and it lives here so they cannot drift apart
 * from each other while all four claim to mirror the same number.
 */
export const MIN_PASSWORD_LENGTH = 12;

export type PlayerResume =
  | {
      mode: "archive";
      presentation_id?: string | null;
      segment_id: number | null;
      item_position_us: number;
      source_at: string | null;
      filter?: Record<string, string | number | boolean>;
    }
  | { mode: "live"; source_at: string | null }
  | { mode: "dvr"; source_at: string };

/** Player state stored against the account, so it follows the listener between browsers. */
export interface PlayerPrefs {
  volume: number;
  muted: boolean;
  position_ms: number;
  at_live: boolean;
  resume: PlayerResume | null;
}

export interface User {
  id: number;
  username: string;
  role: Role;
  /** Null for operator-made accounts that predate self-registration. */
  email: string | null;
  /** When a confirmation link came back. Null means the address is unproven. */
  email_verified_at: string | null;
  active: boolean;
  created_at: string | null;
  last_login: string | null;
  /** Null until this account has stored a position; the browser's own state stands. */
  prefs: PlayerPrefs | null;
  /** Opaque precondition for conflict-safe whole-document preference writes. */
  prefs_revision: string;
  /** Empty by default; `displayNameOf` is what decides what to actually show. */
  display_name: string | null;
  /** The account's UI language. `"en"` for every row that predates this column. */
  language: Lang;
  avatar_artwork_id: number | null;
  /** Derived server-side from `avatar_artwork_id`; `/api/artwork/<id>` or null. */
  avatar_url: string | null;
  dismissals?: string[];
}

/** One external account linked to this user, from `GET /api/auth/sso/identities`. */
export interface Identity {
  provider: string;
  /** What the provider asserted at link time; not necessarily `User.email`. */
  email: string | null;
  created_at: string;
  last_login_at: string | null;
}

/**
 * What to call an account.
 *
 * One function rather than `user.display_name ?? user.username` at each site: the column is
 * nullable *and* trimmable to nothing, so there are two empty cases, and a site that checks
 * only for null renders a blank corner for anyone who saved a space.
 */
export function displayNameOf(user: Pick<User, "username" | "display_name">): string {
  return user.display_name?.trim() || user.username;
}

/**
 * What an account with no uploaded picture shows.
 *
 * The turntable, not `Artwork`'s hashed initials: initials are for distinguishing thousands
 * of tracks from each other, and an account is looking at its own name already.
 */
export const DEFAULT_AVATAR_URL = "/default-avatar.png";
